import type { RefetchOptions } from "@tanstack/react-query";
import { motion, type Variants } from "motion/react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { HeroIcon } from "../hero-icon";
import { Button } from "../ui/button";

export type LinkError = "linkExpired" | "linkFetch";

type LinkErrorProps = {
  error: LinkError;
  onRefetch?: (options?: RefetchOptions) => Promise<unknown>;
};

const containerVariants: Variants = {
  visible: {
    transition: { staggerChildren: 0.12, ease: "easeOut" },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, transform: "translateY(12px)" },
  visible: {
    opacity: 1,
    transform: "translateY(0px)",
    transition: { damping: 15, stiffness: 50 },
  },
};

export const LinkError = ({ error, onRefetch }: LinkErrorProps) => {
  const t = useTranslations("receipt");
  const tc = useTranslations("common");

  const handleRefetch = async () => {
    if (!onRefetch) return;
    try {
      await onRefetch({ throwOnError: true });
    } catch {
      toast.error(tc("retryError"));
    }
  };

  const errorMeta = {
    title: t(`${error}.title`),
    message: t(`${error}.message`),
  };
  return (
    <div className="m-auto mt-24 max-w-lg px-4 pb-4">
      <motion.div
        variants={containerVariants}
        className="flex flex-col space-y-4"
        initial="hidden"
        animate="visible"
      >
        <div className="mb-8">
          <motion.div variants={itemVariants}>
            <HeroIcon
              name="exclamation-circle"
              iconStyle="solid"
              className="h-20 w-20 text-red-500"
            />
          </motion.div>
          <motion.p className="font-medium text-2xl" variants={itemVariants}>
            {errorMeta.title}
          </motion.p>
        </div>
        <motion.p className="text-muted" variants={itemVariants}>
          {errorMeta.message}
        </motion.p>
        {error === "linkFetch" && (
          <motion.div variants={itemVariants}>
            <Button
              className="cursor-pointer active:scale-[0.97]"
              onClick={handleRefetch}
            >
              {tc("retry")}
            </Button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};
